import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: any) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        // Horizontal padding keeps the centered month/year dropdowns clear of the
        // prev/next arrows, which are absolutely positioned at the caption edges.
        caption: "flex justify-center pt-1 relative items-center px-9",
        caption_label: "text-sm font-medium",
        caption_dropdowns: "flex items-center justify-center gap-1.5",
        vhidden: "sr-only",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-60 hover:opacity-100 transition-opacity"
        ),
        nav_button_previous: "absolute left-0",
        nav_button_next: "absolute right-0",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-2xl",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-2xl [&:has(>.day-range-start)]:rounded-l-2xl first:[&:has([aria-selected])]:rounded-l-2xl last:[&:has([aria-selected])]:rounded-r-2xl"
            : "[&:has([aria-selected])]:rounded-2xl"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 rounded-2xl p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-grad-hot text-white shadow-[0_6px_14px_rgba(255,77,141,0.35)] hover:bg-grad-hot hover:text-white focus:bg-grad-hot focus:text-white",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }: any) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }: any) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
        // Styled month/year dropdowns (used when captionLayout="dropdown-buttons").
        // Replaces react-day-picker's default overlay control, which relied on the
        // library's stylesheet (not imported here) and left the year picker unusable.
        Dropdown: ({ value, onChange, children, name }: any) => (
          <select
            value={value}
            onChange={onChange}
            aria-label={name === "months" ? "Month" : "Year"}
            className="h-7 cursor-pointer rounded-lg border border-[#1b1033]/10 bg-white px-1.5 text-sm font-medium text-[#1b1033] outline-none transition-colors hover:border-[#1b1033]/25 focus:ring-2 focus:ring-[#ff4d8d]/40 dark:border-white/15 dark:bg-[#1e1438] dark:text-[#f4f0ff] motion-reduce:transition-none"
          >
            {children}
          </select>
        ),
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
